import * as Sentry from '@sentry/browser'
import { pageData } from '@/util/page_data'
import { ignoreErrors, isNoise, trackTurboHandledErrors } from './noise'

// # NB! The config for the backend is in config/initializers/sentry.rb
// @ts-ignore
let env = import.meta.env['VITE_RUBY_MODE'] // NB, this is set at compile time
const { sentry } = pageData()
const applicationKey: string = import.meta.env.VITE_SENTRY_APPLICATION_KEY
if (sentry) {
  trackTurboHandledErrors()

  Sentry.init({
    dsn: sentry.dsn,
    environment: env,

    // Collecting client IP addresses is a privacy decision of its own, not one to take as an SDK default.
    dataCollection: { userInfo: false },
    // A stack trace changes how Sentry groups captureMessage events, splitting their existing issues into new ones.
    attachStacktrace: false,

    integrations: (defaults) => [
      // BrowserApiErrors wraps every setTimeout, requestAnimationFrame and event listener in a function of our bundle,
      // so an error from a third-party script's callback would always have one frame of ours and never be tagged.
      // Our modules load with CORS, so the global handlers get the full stack of our own errors without it, and a
      // third-party script loaded without CORS, such as Stripe.js, reaches them only as "Script error.", which the SDK
      // ignores.
      ...defaults.filter((integration) => integration.name !== 'BrowserApiErrors'),
      ...(applicationKey
        ? [
            Sentry.thirdPartyErrorFilterIntegration({
              filterKeys: [applicationKey],
              behaviour: 'apply-tag-if-exclusively-contains-third-party-frames',
            }),
          ]
        : []),
    ],
    ignoreErrors,
    beforeSend: (event, hint) => (isNoise(event, hint) ? null : event),
  })

  // Sentry keeps whatever it was last told, so a Turbo visit that switches company has to say so again or
  // every later error is tagged with the company you left.
  const applyIdentity = () => {
    const { user, company } = pageData()

    if (user.email) Sentry.setUser({ email: user.email })

    if (company.hashid) {
      Sentry.setContext('Company', { name: company.name, hashid: company.hashid })
      Sentry.setTag('company', company.name)
    }
  }

  applyIdentity()
  document.addEventListener('turbo:load', applyIdentity)
  // Make Sentry available globally for compatibility
  // @ts-ignore
  window.Sentry = Sentry
}
